import React, { useEffect, useState } from 'react';

interface GhastPosition {
  id: number;
  x: number;
  y: number;
  directionX: number; // Will be either 1 (right) or -1 (left)
  directionY: number; // Will be either 1 (down) or -1 (up) or 0 (no vertical movement)
  speed: number;
  size: number;
  facingRight: boolean; // Track which direction the ghast is facing
  originalDirectionX: number; // Store the original horizontal direction
  originalDirectionY: number; // Store the original vertical direction
  directionChangeTimer: number; // Timer for direction changes
  isDirectionChanged: boolean; // Track if direction is temporarily changed
}

const HappyGhast: React.FC = () => {
  const [ghasts, setGhasts] = useState<GhastPosition[]>([]);

  // Initialize random number of ghasts (between 3-8)
  useEffect(() => {
    const numGhasts = Math.floor(Math.random() * 6) + 3; // Random number between 3-8
    const initialGhasts: GhastPosition[] = [];

    for (let i = 0; i < numGhasts; i++) {
      const facingRight = Math.random() > 0.5; // Random direction
      const directionX = facingRight ? 1 : -1; // Move right or left
      const directionY = Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0; // 30% chance of vertical movement
      
      initialGhasts.push({
        id: i,
        x: facingRight ? -100 : window.innerWidth + 100, // Start off-screen horizontally
        y: directionY === 0 ? Math.random() * (document.documentElement.scrollHeight - 200) + 100 : 
           directionY > 0 ? -100 : document.documentElement.scrollHeight + 100, // Use full document height
        directionX: directionX,
        directionY: directionY,
        speed: Math.random() * 0.8 + 0.5, // Random speed between 0.5-1.3
        size: Math.random() * 40 + 40, // Random size between 40-80px
        facingRight: facingRight,
        originalDirectionX: directionX, // Store original horizontal direction
        originalDirectionY: directionY, // Store original vertical direction
        directionChangeTimer: 0, // Timer for direction changes
        isDirectionChanged: false, // Track if direction is temporarily changed
      });
    }

    setGhasts(initialGhasts);
  }, []);

  // Animation loop for ghast movement
  useEffect(() => {
    let running = true
    let intervalId: number | null = null
    
    const animateGhasts = () => {
      if (!running) return
      setGhasts(prevGhasts => 
        prevGhasts.map(ghast => {
          let newX = ghast.x + ghast.directionX * ghast.speed;
          let newY = ghast.y + ghast.directionY * ghast.speed;
          let newDirectionX = ghast.directionX;
          let newDirectionY = ghast.directionY;
          let newFacingRight = ghast.facingRight;
          let newDirectionChangeTimer = ghast.directionChangeTimer;
          let newIsDirectionChanged = ghast.isDirectionChanged;
          
          // Boundary check for bottom area (footer/ground) - use document height
          const bottomBoundary = document.documentElement.scrollHeight - 200; // Keep 200px from bottom
          if (newY + ghast.size > bottomBoundary) {
            newY = bottomBoundary - ghast.size;
            newDirectionY = -1; // Bounce back up
          }
          
          // Boundary check for top area
          if (newY < 50) {
            newY = 50;
            newDirectionY = 1; // Bounce back down
          }
          
          // Handle direction change timer
          if (ghast.isDirectionChanged) {
            newDirectionChangeTimer += 1;
            // After about 2-4 seconds (40-80 frames at 50ms intervals), revert to original direction
            if (newDirectionChangeTimer > Math.random() * 40 + 40) {
              newDirectionX = ghast.originalDirectionX;
              newDirectionY = ghast.originalDirectionY;
              newFacingRight = ghast.originalDirectionX > 0;
              newIsDirectionChanged = false;
              newDirectionChangeTimer = 0;
            }
          } else {
            // Occasionally change direction randomly (0.3% chance per frame)
            if (Math.random() < 0.003) {
              // Randomly change horizontal, vertical, or both directions
              if (Math.random() > 0.5) {
                newDirectionX = -ghast.originalDirectionX; // Move in opposite horizontal direction
                newFacingRight = !ghast.facingRight;
              }
              if (Math.random() > 0.5) {
                newDirectionY = -ghast.originalDirectionY; // Move in opposite vertical direction
              }
              newIsDirectionChanged = true;
              newDirectionChangeTimer = 0;
            }
          }
          
          // Handle off-screen reset for horizontal movement
          if (ghast.originalDirectionX > 0 && newX > window.innerWidth + 100) {
            // Reset to left side, facing right
            return {
              ...ghast,
              x: -100,
              y: ghast.originalDirectionY === 0 ? Math.random() * (document.documentElement.scrollHeight - 200) + 100 : 
                  ghast.originalDirectionY > 0 ? -100 : document.documentElement.scrollHeight + 100,
              speed: Math.random() * 0.8 + 0.5, // New random speed
              directionX: 1, // Always start moving right
              directionY: ghast.originalDirectionY, // Keep original vertical direction
              facingRight: true,
              originalDirectionX: 1,
              originalDirectionY: ghast.originalDirectionY,
              directionChangeTimer: 0,
              isDirectionChanged: false,
            };
          } else if (ghast.originalDirectionX < 0 && newX < -100) {
            // Reset to right side, facing left
            return {
              ...ghast,
              x: window.innerWidth + 100,
              y: ghast.originalDirectionY === 0 ? Math.random() * (document.documentElement.scrollHeight - 200) + 100 : 
                  ghast.originalDirectionY > 0 ? -100 : document.documentElement.scrollHeight + 100,
              speed: Math.random() * 0.8 + 0.5, // New random speed
              directionX: -1, // Always start moving left
              directionY: ghast.originalDirectionY, // Keep original vertical direction
              facingRight: false,
              originalDirectionX: -1,
              originalDirectionY: ghast.originalDirectionY,
              directionChangeTimer: 0,
              isDirectionChanged: false,
            };
          }
          
          // Handle off-screen reset for vertical movement (from both top and bottom)
          if (ghast.originalDirectionY > 0 && newY > document.documentElement.scrollHeight - 200) {
            // Reset to top (avoid bottom area)
            return {
              ...ghast,
              x: ghast.originalDirectionX === 0 ? Math.random() * (window.innerWidth - 200) + 100 : 
                  ghast.originalDirectionX > 0 ? -100 : window.innerWidth + 100,
              y: -100,
              speed: Math.random() * 0.8 + 0.5, // New random speed
              directionX: ghast.originalDirectionX, // Keep original horizontal direction
              directionY: 1, // Always start moving down
              facingRight: ghast.originalDirectionX > 0,
              originalDirectionX: ghast.originalDirectionX,
              originalDirectionY: 1,
              directionChangeTimer: 0,
              isDirectionChanged: false,
            };
          } else if (ghast.originalDirectionY < 0 && newY < -100) {
            // Reset to bottom (avoid top area)
            return {
              ...ghast,
              x: ghast.originalDirectionX === 0 ? Math.random() * (window.innerWidth - 200) + 100 : 
                  ghast.originalDirectionX > 0 ? -100 : window.innerWidth + 100,
              y: document.documentElement.scrollHeight + 100, // Reset to bottom
              speed: Math.random() * 0.8 + 0.5, // New random speed
              directionX: ghast.originalDirectionX, // Keep original horizontal direction
              directionY: -1, // Always start moving up
              facingRight: ghast.originalDirectionX > 0,
              originalDirectionX: ghast.originalDirectionX,
              originalDirectionY: -1,
              directionChangeTimer: 0,
              isDirectionChanged: false,
            };
          }

          return {
            ...ghast,
            x: newX,
            y: newY,
            directionX: newDirectionX,
            directionY: newDirectionY,
            facingRight: newFacingRight,
            directionChangeTimer: newDirectionChangeTimer,
            isDirectionChanged: newIsDirectionChanged,
          };
        })
      );
    };

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - pause animation
        running = false
        if (intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      } else {
        // Page is visible - resume animation
        running = true
        intervalId = setInterval(animateGhasts, 50)
      }
    }

    // Start animation
    intervalId = setInterval(animateGhasts, 50); // Update every 50ms for smooth movement

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      running = false
      if (intervalId) clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    };
  }, []);

  return (
    <div style={{ 
      position: 'absolute', 
      left: 0, 
      top: 0, 
      width: document.documentElement.scrollWidth, 
      height: document.documentElement.scrollHeight, 
      pointerEvents: 'none', 
      zIndex: 1,
      overflow: 'hidden'
    }}>
      {ghasts.map(ghast => (
        <img
          key={ghast.id}
          src="/613px-Happy_Ghast_pixel_art.png"
          alt="Happy Ghast"
          style={{
            position: 'absolute',
            left: `${ghast.x}px`,
            top: `${ghast.y}px`,
            width: `${ghast.size}px`,
            height: `${ghast.size}px`,
            transition: 'none', // Disable CSS transitions for smooth animation
            filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.2))', // Subtle glow effect
            transform: ghast.facingRight ? 'scaleX(1)' : 'scaleX(-1)', // Flip horizontally based on direction
          }}
        />
      ))}
    </div>
  );
};

export default HappyGhast; 